// Лента провалов одного судна: период в одну полоску, провалы на своих местах.
//
// Раскрывается из таблицы «Суда с наибольшим молчанием» — там же, где судно
// названо. Ключ — MMSI: именно им живут данные АИС.
//
// Почему ленты нет на карточке судна в разделе «Флот»: там суда ключуются
// unit_key из данных вылова и названы кириллицей, в АИС — MMSI и латиница.
// Однозначной транслитерацией связались 57 юнитов из 159 (10 неоднозначно), а
// из 15 своих судов с АИС в юнитах флота нашлись 2 — популяции почти не
// пересекаются. Связывать их угадыванием имени значило бы приписывать одному
// судну молчание другого; нужен явный справочник, см.
// docs/plans/2026-08-02-ring-calibration-nogo.md.

import { useQuery } from '@tanstack/react-query'
import { useT } from '../i18n'
import { cabinetApi } from '../api'
import { formatHours, formatMinutes } from './obsFormat'
import { ribbonSegments } from './gapRibbon'

const DAY_MS = 24 * 60 * 60 * 1000

function dayTicks(from: string, to: string): { pct: number; label: string }[] {
  const start = Date.parse(from)
  const span = Date.parse(to) - start
  if (!(span > 0)) return []
  // Не больше ~6 подписей: на месячной ленте посуточные засечки сливаются.
  const stepDays = Math.max(1, Math.round(span / DAY_MS / 6))
  const ticks: { pct: number; label: string }[] = []
  for (let t = start; t <= start + span; t += stepDays * DAY_MS) {
    const d = new Date(t)
    ticks.push({
      pct: ((t - start) / span) * 100,
      label: `${d.getUTCDate()}.${String(d.getUTCMonth() + 1).padStart(2, '0')}`,
    })
  }
  return ticks
}

export function VesselGapRibbon({
  mmsi,
  name,
  from,
  to,
}: {
  mmsi: string
  name: string
  from: string
  to: string
}) {
  const t = useT()
  const { data, isLoading, isError } = useQuery({
    queryKey: ['cabinet', 'observability', 'vessel', mmsi, from, to],
    queryFn: () => cabinetApi.observability.vessel(mmsi, from, to),
  })

  if (isLoading) return <div className="px-2 py-3 text-xs text-mist">{t('obs.ribbon.loading')}</div>
  if (isError) return <div className="px-2 py-3 text-xs text-mist">{t('obs.ribbon.failed')}</div>

  const gaps = data?.gaps ?? []
  const segments = ribbonSegments(gaps, from, to)
  const ticks = dayTicks(from, to)

  return (
    <div className="px-2 pb-3 pt-1">
      <div className="mb-1.5 flex flex-wrap items-baseline gap-x-3 text-[11px] text-mist">
        <span className="text-foam">{name}</span>
        <span>{t('obs.gaps')}: {gaps.length}</span>
        <span>
          {t('obs.map.silence')}: {formatHours(data?.blind_hours ?? 0)}
        </span>
      </div>

      <div className="relative h-6 overflow-hidden rounded bg-hull/30">
        {segments.map((seg, i) => (
          <div
            key={`${seg.gap.start}-${i}`}
            title={
              `${new Date(seg.gap.start).toISOString().slice(0, 16).replace('T', ' ')} UTC · ` +
              `${formatMinutes(seg.gap.duration_min)} · ` +
              `${seg.gap.start_lat.toFixed(2)}, ${seg.gap.start_lon.toFixed(2)}` +
              (seg.gap.low_confidence ? ` · ${t('obs.ribbon.estimatedThreshold')}` : '')
            }
            className={`absolute top-0 h-full ${
              seg.gap.low_confidence ? 'bg-sonar/40' : 'bg-sonar/80'
            }`}
            style={{ left: `${seg.leftPct}%`, width: `${seg.widthPct}%` }}
          />
        ))}
        {gaps.length === 0 ? (
          <div className="flex h-full items-center justify-center text-[10px] text-mist">
            {t('obs.ribbon.none')}
          </div>
        ) : null}
      </div>

      <div className="relative mt-1 h-3">
        {ticks.map(tick => (
          <span
            key={tick.pct}
            className="absolute -translate-x-1/2 text-[9px] text-mist"
            style={{ left: `${tick.pct}%` }}
          >
            {tick.label}
          </span>
        ))}
      </div>
    </div>
  )
}
